import { AppSettingType, FeatureManagementType, RebarEstimationStatus, ScenarioMetadata } from "@api/apiClient";
import { CustomGridTreeDataGroupingCell } from "@components/DataGrid/components/cell/CustomGridTreeDataGroupingCell";
import QuantificationGridHeader from "@components/DataGrid/components/headers/QuantificationGridHeader";
import VersionChip from "@components/VersionChip/VersionChip";
import i18n from "@i18n/i18n";
import { Theme } from "@mui/material";
import { GRID_TREE_DATA_GROUPING_FIELD, GridApi, GridColDef, GridGroupNode, GridRenderCellParams } from "@mui/x-data-grid-premium";
import AppStore from "@stores/AppStore";
import _ from "lodash";
import { MutableRefObject } from "react";

const defaultImpactColumns = ["totalCo2"];
const adStageImpactColumns = ['a1A2A3', 'a4A5', 'a', 'b1', 'b2B5', 'b', 'biogenic', 'c', 'd', 'totalCo2'];

export const getColumns = (apiRef: MutableRefObject<GridApi>, appStore: AppStore, theme: Theme): GridColDef[] => {
  const columns = getDefaultColumns(apiRef, appStore, theme);

  const impactColumns = appStore.hasFeature(FeatureManagementType.ResultsADStages) ? adStageImpactColumns : defaultImpactColumns;
  const impactColumnsToInsert = impactColumns.map((col) => getCo2eColumnDef(appStore, col));

    // Insert the impact columns starting at the position of the rebarMaterial column
  const index = columns.findIndex((col) => col.field === "tag");

  columns.splice(index, 0, ...impactColumnsToInsert);

  if(appStore.hasFeature(FeatureManagementType.AdditionalMetadata)) {
    const metadataColumns = getScenarioMetadataColumnDef();

    // Insert the metadata columns after the percentAssigned column
    const metadataInsertIndex = columns.findIndex((col) => col.field === "percentAssigned");
    columns.splice(metadataInsertIndex + 1, 0, ...metadataColumns);
  }

  return columns;
};

export const getDefaultColumns = (_apiRef: MutableRefObject<GridApi>, _appStore: AppStore, _theme: Theme): GridColDef[] => [
  /*
  {
    field: "key",
    headerName: i18n.t("results-grid.columns.key"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("results-grid.columns.key")} />,
    flex: 1,
    valueGetter: (_value: any, row: any) => {
      return row.key;
    }
  },
  */
  {
    field: "tag",
    headerName: i18n.t("results-grid.columns.tag"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("results-grid.columns.tag")} />,
    flex: 1,
    renderCell: (params) => {
      return <VersionChip label={params.row.data.tag} />;
    },
  },
  /* A-D Impact Columns will be inserted here ... */
  /* A-D Impact Columns will be inserted here ... */
  /* A-D Impact Columns will be inserted here ... */

  /*
  {
    field: "percentOfEmissionReductionTarget",
    headerName: i18n.t("quantification-header", { name: i18n.t("results-grid.columns.percent-of-emission-reduction-target"), unit: i18n.t("units.%") }),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("results-grid.columns.percent-of-emission-reduction-target")} unit={i18n.t("units.%")} />,
    flex: 1,
    valueGetter: (_value: any, row: any) => {
      return row.data.percentOfEmissionReductionTarget;
    },
    valueFormatter: (value?: number) => {
      return i18n.t("percent", { value });
    },
  },
  */
  {
    field: "apsVersion",
    headerName: i18n.t("results-grid.columns.version"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("results-grid.columns.version")} />,
    flex: 1,
    valueGetter: (_value: any, row: any) => {
      return row.data.model?.apsVersion;
    },
    renderCell: (params) => {
      if (!params.row.data?.model?.apsVersion) {
        return null;
      }
      return <VersionChip label={params.row.data?.model?.modelVersion || ""} />;
    },
  },
  {
    field: "percentRebar",
    headerName: i18n.t("quantification-header", { name: i18n.t("results-grid.columns.percent-rebar"), unit: i18n.t("units.%") }),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("results-grid.columns.percent-rebar")} unit={i18n.t("units.%")} />,
    flex: 1,
    valueGetter: (_value: any, row: any) => {
      if (row.data?.rebarEstimationStatus === RebarEstimationStatus.Enabled) {
        return i18n.t("scnenario-grid.percent-rebar.enabled");
      }
      if (row.data?.rebarEstimationStatus === RebarEstimationStatus.Estimated) {
        return i18n.t("scnenario-grid.percent-rebar.estimated");
      }
    },
  },
  {
    field: "fullName",
    //headerName: i18n.t("quantification-header", { name: i18n.t("scenarios-grid.columns.percent-assigned"), unit: i18n.t("units.%") }),
    headerName: i18n.t("results-grid.columns.fullName"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("results-grid.columns.fullName")} />,
    flex: 1,

    valueGetter: (_value: any, row: any) => {
      return  ( row?.data.fullName);
    },
  },
  {
    field: "createdAt",
    headerName: i18n.t("results-grid.columns.create-date"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("results-grid.columns.create-date")} />,
    flex: 1,
    type: "number",
    renderCell: (params: any) => {
        return i18n.t("datetime_short", { value: new Date(params.row.data.createdAt) });
      },
  },
  {
    field: "percentAssigned",
    headerName: i18n.t("quantification-header", { name: i18n.t("results-grid.columns.percent-assigned"), unit: i18n.t("units.%") }),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("results-grid.columns.percent-assigned")} unit={i18n.t("units.%")} />,
    flex: 1,
    type: "number",
    valueGetter: (_value: any, row: any) => {
      return row.data?.percentAssigned;
    },
    valueFormatter: (value?: number) => {
      return i18n.t("percent", { value });
    },
  },
];

export const getA1A2A3Co2eColumnDef = (): GridColDef => {
  return {
    field: "a1A2A3",
    headerName: i18n.t("quantification-header", { name: i18n.t("results-grid.columns.a1A2A3"), unit: i18n.t("units.tCO2e") }),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("results-grid.columns.a1A2A3")} unit={i18n.t("units.tCO2e")} />,
    flex: 1,
    type: "number",
    valueGetter: (_value: any, row: any) => {
      const a1a2a3 = row?.data?.co2Aggregates?.a1A2A3;

      if (a1a2a3 == null) {
        return null;
      }

      return a1a2a3 || 0;
    },
    valueFormatter: (value?: number) => {
      if (!_.isFinite(value)) return;
      return i18n.t("number", { value, digits: AppSettingType.NumberFormat_EC_Digits });
    },
  };
};

export const getACo2eColumnDef = (): GridColDef => {
  return {
    field: "a",
    headerName: i18n.t("quantification-header", { name: i18n.t("results-grid.columns.a"), unit: i18n.t("units.tCO2e") }),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("results-grid.columns.a")} unit={i18n.t("units.tCO2e")} />,
    flex: 1,
    type: "number",
    valueGetter: (_value: any, row: any) => {
      const a1a2a3 = row?.data?.co2Aggregates?.a1A2A3;
      const a4 = row?.data?.co2Aggregates?.a4;
      const a5 = row?.data?.co2Aggregates?.a5;

      if (a1a2a3 == null && a4 == null && a5 == null) {
        return null;
      }

      return (a1a2a3 || 0) + (a4 || 0) + (a5 || 0);
    },
    valueFormatter: (value?: number) => {
      if (!_.isFinite(value)) return;
      return i18n.t("number", { value, digits: AppSettingType.NumberFormat_EC_Digits });
    },
  };
};

export const getA4A5Co2eColumnDef = (): GridColDef => {
  return {
    field: "a4A5",
    headerName: i18n.t("quantification-header", { name: i18n.t("results-grid.columns.a4A5"), unit: i18n.t("units.tCO2e") }),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("results-grid.columns.a4A5")} unit={i18n.t("units.tCO2e")} />,
    flex: 1,
    type: "number",
    valueGetter: (_value: any, row: any) => {
      const a4 = row?.data?.co2Aggregates?.a4;
      const a5 = row?.data?.co2Aggregates?.a5;

      if (a4 == null && a5 == null) {
        return null;
      }

      return (a4 || 0) + (a5 || 0);
    },
    valueFormatter: (value?: number) => {
      if (!_.isFinite(value)) return;
      return i18n.t("number", { value, digits: AppSettingType.NumberFormat_EC_Digits });
    },
  };
};

export const getB1Co2eColumnDef = (): GridColDef => {
  return {
    field: "b1",
    headerName: i18n.t("quantification-header", { name: i18n.t("results-grid.columns.b1"), unit: i18n.t("units.tCO2e") }),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("results-grid.columns.b1")} unit={i18n.t("units.tCO2e")} />,
    flex: 1,
    type: "number",
    valueGetter: (_value: any, row: any) => {
      const b1 = row?.data?.co2Aggregates?.b1;

      if (b1 == null) {
        return null;
      }

      return b1 || 0;
    },
    valueFormatter: (value?: number) => {
      if (!_.isFinite(value)) return; 
      return i18n.t("number", { value, digits: AppSettingType.NumberFormat_EC_Digits });
    },
  };
};

export const getB2B5Co2eColumnDef = (): GridColDef => {
  return {
    field: "b2B5",
    headerName: i18n.t("quantification-header", { name: i18n.t("results-grid.columns.b2B5"), unit: i18n.t("units.tCO2e") }),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("results-grid.columns.b2B5")} unit={i18n.t("units.tCO2e")} />,
    flex: 1,
    type: "number",
    valueGetter: (_value: any, row: any) => {
      const b2 = row?.data?.co2Aggregates?.b2;
      const b3 = row?.data?.co2Aggregates?.b3;
      const b4 = row?.data?.co2Aggregates?.b4;
      const b5 = row?.data?.co2Aggregates?.b5;

      if (b2 == null && b3 == null && b4 == null && b5 == null) {
        return null;
      }

      return (b2 || 0) + (b3 || 0) + (b4 || 0) + (b5 || 0);
    },
    valueFormatter: (value?: number) => {
      if (!_.isFinite(value)) return; 
      return i18n.t("number", { value, digits: AppSettingType.NumberFormat_EC_Digits });
    },
  };
};

export const getBiogenicCo2eColumnDef = (): GridColDef => {
  return {
    field: "biogenic",  
    headerName: i18n.t("quantification-header", { name: i18n.t("results-grid.columns.biogenic"), unit: i18n.t("units.tCO2e") }),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("results-grid.columns.biogenic")} unit={i18n.t("units.tCO2e")} />,
    flex: 1,
    type: "number",
    valueGetter: (_value: any, row: any) => {
      const biogenic = row?.data?.co2Aggregates?.biogenic;

      if (biogenic == null) {
        return null;
      }

      return biogenic || 0;
    },
    valueFormatter: (value?: number) => {
      if (!_.isFinite(value)) return;
      return i18n.t("number", { value, digits: AppSettingType.NumberFormat_EC_Digits });
    },
  };
};

export const getBCo2eColumnDef = (): GridColDef => {
  return {
    field: "b",
    headerName: i18n.t("quantification-header", { name: i18n.t("results-grid.columns.b"), unit: i18n.t("units.tCO2e") }),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("results-grid.columns.b")} unit={i18n.t("units.tCO2e")} />,
    flex: 1,
    type: "number",
    valueGetter: (_value: any, row: any) => {
      const b1 = row?.data?.co2Aggregates?.b1;
      const b2 = row?.data?.co2Aggregates?.b2;
      const b3 = row?.data?.co2Aggregates?.b3;
      const b4 = row?.data?.co2Aggregates?.b4;
      const b5 = row?.data?.co2Aggregates?.b5;
      const b6 = row?.data?.co2Aggregates?.b6;
      const b7 = row?.data?.co2Aggregates?.b7;

      if (b1 == null && b2 == null && b3 == null && b4 == null && b5 == null && b6 == null && b7 == null) {
        return null;
      }

      return (b1 || 0) + (b2 || 0) + (b3 || 0) + (b4 || 0) + (b5 || 0) + (b6 || 0) + (b7 || 0);
    },
    valueFormatter: (value?: number) => {
      if (!_.isFinite(value)) return;
      return i18n.t("number", { value, digits: AppSettingType.NumberFormat_EC_Digits });
    },
  };
};

export const getCCo2eColumnDef = (): GridColDef => {
  return {
    field: "c",
    headerName: i18n.t("quantification-header", { name: i18n.t("results-grid.columns.c"), unit: i18n.t("units.tCO2e") }),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("results-grid.columns.c")} unit={i18n.t("units.tCO2e")} />,
    flex: 1,
    type: "number",
    valueGetter: (_value: any, row: any) => {
      const c1 = row?.data?.co2Aggregates?.c1;
      const c2 = row?.data?.co2Aggregates?.c2;
      const c3 = row?.data?.co2Aggregates?.c3;
      const c4 = row?.data?.co2Aggregates?.c4;

      if (c1 == null && c2 == null && c3 == null && c4 == null) {
        return null;
      }

      return (c1 || 0) + (c2 || 0) + (c3 || 0) + (c4 || 0);
    },
    valueFormatter: (value?: number) => {
      if (!_.isFinite(value)) return;
      return i18n.t("number", { value, digits: AppSettingType.NumberFormat_EC_Digits });
    },
  };
};

export const getDCo2eColumnDef = (): GridColDef => {
  return {
    field: "d",
    headerName: i18n.t("quantification-header", { name: i18n.t("results-grid.columns.d"), unit: i18n.t("units.tCO2e") }),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("results-grid.columns.d")} unit={i18n.t("units.tCO2e")} />,
    flex: 1,
    type: "number",
    valueGetter: (_value: any, row: any) => {
      return row.data?.co2Aggregates?.d;
    },
    valueFormatter: (value?: number) => {
      if (!_.isFinite(value)) return;
      return i18n.t("number", { value, digits: AppSettingType.NumberFormat_EC_Digits });
    },
  };
};

export const getTotalCo2ColumnDef = (_appStore:AppStore): GridColDef => {
   const headerName = _appStore.hasFeature(FeatureManagementType.ADStages)
    ? i18n.t("results-grid.columns.totalCo2-A-D")
    : i18n.t("results-grid.columns.totalCo2");
  return {
    field: "totalCo2",
    headerName: i18n.t("quantification-header", { name: headerName, unit: i18n.t("units.tCO2e") }),
    renderHeader: () => <QuantificationGridHeader name={headerName} unit={i18n.t("units.tCO2e")} />,
    flex: 1,
    type: "number",
    valueGetter: (_value: any, row: any) => {
      return row.data?.co2Aggregates?.totalCo2;
    },
    valueFormatter: (value?: number) => {
      return i18n.t("number", { value, digits: AppSettingType.NumberFormat_EC_Digits });
    },
  };
};

export const getGroupingColumnDef = (headerName: string): GridColDef => {
  return {
    field: "name",
    headerName: headerName,
    renderHeader: () => <QuantificationGridHeader name={headerName} />,
    filterable: true,
    hideable: false,
    renderCell: (params) => {
      return <CustomGridTreeDataGroupingCell {...(params as GridRenderCellParams<any, any, any, GridGroupNode>)} hideDescendantCount={true} />;
    },
    valueGetter: (_value: any, row: any) => row?.data?.name,
  };
};

export const getCo2eColumnDef = (_appStore: AppStore, column: string): GridColDef => {
  switch (column) {
    case "a1A2A3":
      return getA1A2A3Co2eColumnDef();
    case "a4A5":
      return getA4A5Co2eColumnDef();
    case "a":
      return getACo2eColumnDef();
    case "b1":
      return getB1Co2eColumnDef();
    case "b2B5":
      return getB2B5Co2eColumnDef();
    case "biogenic":
      return getBiogenicCo2eColumnDef();
    case "b":
      return getBCo2eColumnDef();
    case "c":
      return getCCo2eColumnDef();
    case "d":
      return getDCo2eColumnDef();
    case "totalCo2":
      return getTotalCo2ColumnDef(_appStore);
  }

  return null as any;
};

export const getScenarioMetadataColumnDef = (): GridColDef[] => [
  {
    field: "totalFloorArea",
    headerName: i18n.t("quantification-header", { name: i18n.t("results-grid.columns.totalFloorArea"), unit: i18n.t("units.m2") }),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("results-grid.columns.totalFloorArea")} unit={i18n.t("units.m2")} />,
    flex: 1,
    valueGetter: (_value: any, row: any) => {
      // SCENARIO-ONLY LOGIC: Return value only for scenario rows
      if (row.type === "scenario") {
        return row.data?.scenarioMetadata.totalFloorArea;
      }
      return null; // or empty string for model rows
    },
     valueFormatter: (value?: number) => {
      if (!_.isFinite(value)) return;
      return i18n.t("number", { value});
    },
  },
  {
    field: "serviceLife",
    headerName: i18n.t("results-grid.columns.serviceLife"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("results-grid.columns.serviceLife")} />,
    flex: 1,
    valueGetter: (_value: any, row: any) => {
      // SCENARIO-ONLY LOGIC: Return value only for scenario rows
      if (row.type === "scenario") {
        return row.data?.scenarioMetadata.serviceLife;
      }
      return null; // or empty string for model rows
    },
     valueFormatter: (value?: number) => {
      if (!_.isFinite(value)) return;
      return i18n.t("number", { value});
    },
  }  
];

const generateColumns = (srcArr: string[], addArr: string[], insertAfter: string): string[] => {
  const newArr = [];
  const insertIndex = srcArr.indexOf(insertAfter);
  if (insertIndex === -1) {
    newArr.push(...srcArr);
    newArr.push(...addArr.map((col) => `${col}`));
  } else {
    for (let i = 0; i < srcArr.length; i++) {
      if (i === insertIndex) {
        newArr.push(srcArr[i]);
        newArr.push(...addArr.map((col) => `${col}`));
      } else {
        newArr.push(srcArr[i]);
      }
    }
  }
  return newArr;
};

export const getFilteredColumns = (columns: GridColDef[], appStore: AppStore): { filteredColumns: GridColDef[]; columnVisibilityModel: Record<string, boolean> } => {
  let filteredColumns = [...columns];
  let defaultVisibleColumns = [
    "__check__",
    GRID_TREE_DATA_GROUPING_FIELD,
    "label", // TODO: this is the tree view?

    "tag",

    // NOTE: A-D inserted here

    "totalCo2",

    "apsVersion",
    "percentRebar",
    "percentAssigned",
    "fullName",
    "createdAt"
  ];

  const impactColumns = appStore.hasFeature(FeatureManagementType.ResultsADStages) ? adStageImpactColumns : defaultImpactColumns;

  if (appStore.hasFeature(FeatureManagementType.ResultsADStages)) {
    defaultVisibleColumns = generateColumns(defaultVisibleColumns, impactColumns, "tag");
  }

  if(appStore.hasFeature(FeatureManagementType.AdditionalMetadata)) {
    const metadataColumns = ["totalFloorArea", "serviceLife"];
    defaultVisibleColumns.push(...metadataColumns); 
  }

  // Generate column visibility model
  const columnVisibilityModel = filteredColumns.reduce<Record<string, boolean>>((acc, col) => {
    acc[col.field] = defaultVisibleColumns.includes(col.field); // Set true for defaultColumns, false for others
    return acc;
  }, {});

  return { filteredColumns, columnVisibilityModel };
};
